import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { submissionsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './SubmissionWorkflow.css';

function SubmissionWorkflow() {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [filterStatus, setFilterStatus] = useState('submit');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showMoveToVote1Modal, setShowMoveToVote1Modal] = useState(false);
  const [moveToVote1Data, setMoveToVote1Data] = useState({
    submitId: null,
    gr_desc: '',
    gr_body: '',
    gr_cat_id: '',
  });
  const [approveData, setApproveData] = useState({
    submitId: null,
    gr_num: '',
    gr_sub: '0',
    gr_cat_id: '',
    gr_desc: '',
    gr_body: '',
  });
  const [categories, setCategories] = useState([]);
  const [ruleNumbers, setRuleNumbers] = useState([]);
  const [suggestedGrNum, setSuggestedGrNum] = useState(null);
  const [vote2Comments, setVote2Comments] = useState({}); // submit_id -> comment text
  const [savingComment, setSavingComment] = useState(null);
  const [statusCounts, setStatusCounts] = useState([]); // [{ status, count }, ...] from API

  const loadSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      let response;
      if (filterStatus === 'vote1') {
        response = await submissionsApi.getVote1Submissions();
      } else if (filterStatus === 'vote2') {
        response = await submissionsApi.getVote2Submissions();
      } else {
        response = await submissionsApi.getAllSubmissions(filterStatus);
      }
      
      if (response.data.success) {
        setSubmissions(response.data.data || []);
        // Load comments for vote2 submissions
        if (filterStatus === 'vote2') {
          const commentsMap = {};
          response.data.data.forEach(sub => {
            if (sub.comment) {
              commentsMap[sub.submit_id] = sub.comment;
            }
          });
          setVote2Comments(commentsMap);
        }
      } else {
        setError(response.data.message || 'Failed to load submissions');
      }
    } catch (err) {
      console.error('Error loading submissions:', err);
      setError('Failed to load submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await submissionsApi.getCategories();
      if (response.data.success) {
        setCategories(response.data.data || response.data || []);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  }, []);

  const loadRuleNumbers = useCallback(async () => {
    try {
      const response = await submissionsApi.getAvailableRuleNumbers();
      if (response.data.success) {
        setRuleNumbers(response.data.data.rule_numbers || []);
        setSuggestedGrNum(response.data.data.suggested_gr_num || 1);
      }
    } catch (err) {
      console.error('Error loading rule numbers:', err);
    }
  }, []);

  const loadStatusCounts = useCallback(async () => {
    try {
      const response = await submissionsApi.getSubmissionStatusCounts();
      if (response.data.success) {
        setStatusCounts(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading status counts:', err);
    }
  }, []);

  useEffect(() => {
    if (user?.profile === 'admin') {
      loadSubmissions();
      loadCategories();
      loadStatusCounts();
      if (filterStatus === 'vote2') {
        loadRuleNumbers();
      }
    }
  }, [filterStatus, user, loadSubmissions, loadCategories, loadRuleNumbers, loadStatusCounts]);

  // When status counts load, if current filter isn't in the list, switch to first available status
  useEffect(() => {
    if (statusCounts.length > 0 && !statusCounts.some(({ status }) => status === filterStatus)) {
      setFilterStatus(statusCounts[0].status);
    }
  }, [statusCounts]);

  const handleMoveToVote1Click = (submission) => {
    setMoveToVote1Data({
      submitId: submission.submit_id,
      gr_desc: submission.title || '',
      gr_body: submission.rule || '',
      gr_cat_id: submission.gr_cat_id || '',
    });
    setShowMoveToVote1Modal(true);
  };

  const handleMoveToVote1 = async () => {
    if (!moveToVote1Data.gr_cat_id) {
      setError('Please select a category');
      return;
    }

    try {
      setUpdatingStatus(moveToVote1Data.submitId);
      setError('');
      setSuccess('');
      // Rule number and sub are assigned only when council approves (vote2 Approve modal)
      const response = await submissionsApi.moveToVote1(moveToVote1Data.submitId, {
        rule_num: 0,
        gr_sub: 0,
        gr_desc: moveToVote1Data.gr_desc,
        gr_body: moveToVote1Data.gr_body,
        gr_cat_id: parseInt(moveToVote1Data.gr_cat_id),
      });
      
      if (response.data.success) {
        setSuccess('Submission moved to vote1');
        setShowMoveToVote1Modal(false);
        await loadSubmissions();
        await loadStatusCounts();
      } else {
        setError(response.data.message || 'Failed to move to vote1');
      }
    } catch (err) {
      console.error('Error moving to vote1:', err);
      setError(err.response?.data?.message || 'Failed to move to vote1');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleMoveToVote2 = async (submitId) => {
    try {
      setUpdatingStatus(submitId);
      setError('');
      setSuccess('');
      
      const response = await submissionsApi.moveToVote2(submitId);
      
      if (response.data.success) {
        setSuccess('Submission moved to vote2');
        await loadSubmissions();
        await loadStatusCounts();
      } else {
        setError(response.data.message || 'Failed to move to vote2');
      }
    } catch (err) {
      console.error('Error moving to vote2:', err);
      setError(err.response?.data?.message || 'Failed to move to vote2');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleVote2Vote = async (submitId, vote) => {
    try {
      setError('');
      setSuccess('');
      
      const response = await submissionsApi.vote2(submitId, vote);
      
      if (response.data.success) {
        setSuccess('Vote recorded successfully');
        await loadSubmissions();
      } else {
        setError(response.data.message || 'Failed to record vote');
      }
    } catch (err) {
      console.error('Error recording vote:', err);
      setError(err.response?.data?.message || 'Failed to record vote');
    }
  };

  const handleSaveComment = async (submitId) => {
    const comment = vote2Comments[submitId] || '';
    
    try {
      setSavingComment(submitId);
      setError('');
      setSuccess('');
      
      const response = await submissionsApi.saveVote2Comment(submitId, comment);
      
      if (response.data.success) {
        setSuccess('Comment saved successfully');
      } else {
        setError(response.data.message || 'Failed to save comment');
      }
    } catch (err) {
      console.error('Error saving comment:', err);
      setError(err.response?.data?.message || 'Failed to save comment');
    } finally {
      setSavingComment(null);
    }
  };

  const handleStatusDropdownChange = async (submitId, status) => {
    try {
      setUpdatingStatus(submitId);
      setError('');
      setSuccess('');
      
      const response = await submissionsApi.updateStatusFromDropdown(submitId, status);
      
      if (response.data.success) {
        setSuccess(`Status updated to ${status}`);
        await loadSubmissions();
        await loadStatusCounts();
      } else {
        setError(response.data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleApproveClick = async (submission) => {
    // Load rule numbers if not already loaded
    if (ruleNumbers.length === 0) {
      await loadRuleNumbers();
    }
    
    setApproveData({
      submitId: submission.submit_id,
      gr_num: suggestedGrNum ? suggestedGrNum.toString() : '',
      gr_sub: '0',
      gr_cat_id: submission.gr_cat_id || '',
      gr_desc: submission.title || '',
      gr_body: submission.rule || '',
    });
    setShowApproveModal(true);
  };

  const handleGrNumChange = async (grNum) => {
    setApproveData({ ...approveData, gr_num: grNum });
    
    // If existing gr_num selected, get max gr_sub
    if (grNum && ruleNumbers.includes(parseInt(grNum))) {
      try {
        const response = await submissionsApi.getMaxGrSub(parseInt(grNum));
        if (response.data.success) {
          setApproveData({
            ...approveData,
            gr_num: grNum,
            gr_sub: response.data.data.suggested_gr_sub.toString(),
          });
        }
      } catch (err) {
        console.error('Error getting max gr_sub:', err);
      }
    } else {
      setApproveData({ ...approveData, gr_num: grNum, gr_sub: '0' });
    }
  };

  const handleApprove = async () => {
    if (!approveData.gr_num || !approveData.gr_cat_id) {
      setError('Please provide rule number and category');
      return;
    }

    try {
      setApprovingId(approveData.submitId);
      setError('');
      setSuccess('');
      
      const response = await submissionsApi.approveSubmission(
        approveData.submitId,
        {
          gr_num: parseInt(approveData.gr_num),
          gr_sub: parseInt(approveData.gr_sub) || 0,
          gr_cat_id: parseInt(approveData.gr_cat_id),
          gr_desc: approveData.gr_desc,
          gr_body: approveData.gr_body,
        }
      );
      
      if (response.data.success) {
        setSuccess('Submission approved and added to rules!');
        setShowApproveModal(false);
        await loadSubmissions();
        await loadStatusCounts();
      } else {
        setError(response.data.message || 'Failed to approve submission');
      }
    } catch (err) {
      console.error('Error approving submission:', err);
      setError(err.response?.data?.message || 'Failed to approve submission');
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (submitId) => {
    if (!window.confirm('Are you sure you want to reject this submission?')) {
      return;
    }

    try {
      setUpdatingStatus(submitId);
      setError('');
      setSuccess('');
      
      const response = await submissionsApi.rejectSubmission(submitId);
      
      if (response.data.success) {
        setSuccess('Submission rejected');
        await loadSubmissions();
        await loadStatusCounts();
      } else {
        setError(response.data.message || 'Failed to reject submission');
      }
    } catch (err) {
      console.error('Error rejecting submission:', err);
      setError(err.response?.data?.message || 'Failed to reject submission');
    } finally {
      setUpdatingStatus(null);
    }
  };

  if (user?.profile !== 'admin') {
    return (
      <>
        <Header />
        <div className="container">
          <div className="error-message">Unauthorized - Admin access required</div>
        </div>
        <Footer />
      </>
    );
  }

  // Next-status mapping for action buttons (status names come from API, not hardcoded list)
  const statusOptionsMap = {
    submit: 'vote1',
    vote1: 'vote2',
    vote2: null,
    rule: null,
    reject: null,
    hold1: null,
    dup: null,
    revote: null,
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      submit: 'badge-secondary',
      vote1: 'badge-info',
      vote2: 'badge-warning',
      rule: 'badge-success',
      reject: 'badge-danger',
      hold1: 'badge-secondary',
      dup: 'badge-secondary',
      revote: 'badge-secondary',
    };
    return classes[status] || 'badge-secondary';
  };

  /** Get status color from status table (statusCounts from API) for use in badges/buttons */
  const getStatusColor = (status) => {
    if (!status) return null;
    const found = statusCounts.find(sc => String(sc.status).toLowerCase() === String(status).toLowerCase());
    const color = found?.color;
    return color && typeof color === 'string' && color.trim() ? color.trim() : null;
  };

  const getNextStatus = (currentStatus) => statusOptionsMap[currentStatus] ?? null;

  // Split statuses: workflow row (submit, vote1, vote2, rule) then other statuses
  const WORKFLOW_STATUS_ORDER = ['submit', 'vote1', 'vote2', 'rule'];
  const workflowStatuses = WORKFLOW_STATUS_ORDER.map(s => {
    const found = statusCounts.find(sc => sc.status.toLowerCase() === s);
    return found || { status: s, count: 0 };
  });
  const otherStatuses = statusCounts.filter(
    sc => !WORKFLOW_STATUS_ORDER.includes(sc.status.toLowerCase())
  );

  const renderStatusButton = (item) => {
    const { status, count, color } = item;
    const useStatusColor = color && color.trim();
    const buttonStyle = useStatusColor
      ? { backgroundColor: useStatusColor, borderColor: useStatusColor, color: '#fff' }
      : undefined;
    return (
      <button
        key={status}
        type="button"
        className={`btn status-filter-btn ${filterStatus === status ? 'active' : ''} ${!useStatusColor ? getStatusBadgeClass(status) : ''}`}
        style={buttonStyle}
        onClick={() => setFilterStatus(status)}
      >
        <span className="status-name">{status}</span>
        <span className="status-count">({count})</span>
      </button>
    );
  };

  return (
    <>
      <Header />
      <div className="container submission-workflow-container">
        <div className="workflow-header">
          <h1>Submission Workflow Management</h1>
          <Link to="/admin" className="btn btn-secondary">
            <i className="bi bi-arrow-left"></i> Back to GR Admin
          </Link>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            {success}
          </div>
        )}

        <div className="status-filter-buttons">
          <label className="status-filter-label">Filter by status:</label>
          <div className="status-filter-workflow-row">
            <span className="status-filter-row-label">Submission workflow:</span>
            <div className="status-buttons-grid">
              {workflowStatuses.map(renderStatusButton)}
            </div>
          </div>
          {otherStatuses.length > 0 && (
            <div className="status-filter-other-row">
              <span className="status-filter-row-label">Other statuses:</span>
              <div className="status-buttons-grid">
                {otherStatuses.map(renderStatusButton)}
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading">Loading submissions...</div>
        ) : submissions.length === 0 ? (
          <div className="no-submissions">No submissions found with status: {filterStatus}</div>
        ) : (
          <div className="submissions-grid">
            {submissions.map((submission) => {
              const nextStatus = getNextStatus(submission.status);
              const isVote2 = submission.status === 'vote2';
              // API returns id (guy_id value); some responses may use guy_id
              const currentUserGuyId = user?.guy_id ?? user?.id;
              const canVoteVote2 = isVote2 && (currentUserGuyId === 2 || currentUserGuyId === 3);
              
              return (
                <div key={submission.submit_id} className="submission-card">
                  <div className="submission-header">
                    <span
                      className={`badge ${getStatusColor(submission.status) ? '' : getStatusBadgeClass(submission.status)}`}
                      style={getStatusColor(submission.status) ? { backgroundColor: getStatusColor(submission.status), border: `1px solid ${getStatusColor(submission.status)}`, color: '#fff' } : undefined}
                    >
                      {submission.status}
                    </span>
                    <span className="submission-id">ID: {submission.submit_id}</span>
                  </div>
                  
                  {/* Reject button at top for vote2 */}
                  {isVote2 && (
                    <div className="submission-actions-top">
                      <button
                        className="btn btn-danger btn-sm btn-reject"
                        onClick={() => handleReject(submission.submit_id)}
                        disabled={updatingStatus === submission.submit_id}
                      >
                        <i className="bi bi-x-circle"></i> Reject
                      </button>
                    </div>
                  )}
                  
                  <div className="submission-info">
                    <div className="info-row">
                      <strong>Name:</strong> {submission.name}
                    </div>
                    <div className="info-row">
                      <strong>Email:</strong> {submission.email}
                    </div>
                    <div className="info-row">
                      <strong>Date:</strong> {submission.date}
                    </div>
                    <div className="info-row">
                      <strong>Category:</strong> {submission.cat_name || 'N/A'}
                    </div>
                  </div>

                  <div className="submission-content">
                    <h3>{submission.title}</h3>
                    <p>{submission.rule}</p>
                  </div>

                  {/* Vote counts for vote1 */}
                  {submission.status === 'vote1' && (
                    <div className="vote-counts">
                      <span className="vote-count yes">
                        <i className="bi bi-hand-thumbs-up-fill"></i> Yes: {submission.vote1_yes_count || 0}
                      </span>
                      <span className="vote-count no">
                        <i className="bi bi-hand-thumbs-down-fill"></i> No: {submission.vote1_no_count || 0}
                      </span>
                    </div>
                  )}

                  {/* Vote2 voting section */}
                  {isVote2 && (
                    <div className="vote2-section">
                      <div className="vote2-voting">
                        <label>Council Vote:</label>
                        <div className="vote2-buttons">
                          <button
                            className={`btn btn-success btn-sm vote2-btn-up ${
                              canVoteVote2 && 
                              ((currentUserGuyId === 2 && Number(submission.vote_g) === 1) || 
                               (currentUserGuyId === 3 && Number(submission.vote_b) === 1)) 
                              ? 'active' : ''
                            }`}
                            onClick={() => canVoteVote2 && handleVote2Vote(submission.submit_id, 1)}
                            disabled={!canVoteVote2}
                            title={canVoteVote2 ? 'Thumbs Up' : 'Only admins with guy_id 2 or 3 can vote'}
                          >
                            <i className="bi bi-hand-thumbs-up"></i> Up
                          </button>
                          <button
                            className={`btn btn-danger btn-sm vote2-btn-down ${
                              canVoteVote2 && 
                              ((currentUserGuyId === 2 && Number(submission.vote_g) === 0) || 
                               (currentUserGuyId === 3 && Number(submission.vote_b) === 0)) 
                              ? 'active' : ''
                            }`}
                            onClick={() => canVoteVote2 && handleVote2Vote(submission.submit_id, 0)}
                            disabled={!canVoteVote2}
                            title={canVoteVote2 ? 'Thumbs Down' : 'Only admins with guy_id 2 or 3 can vote'}
                          >
                            <i className="bi bi-hand-thumbs-down"></i> Down
                          </button>
                        </div>
                        {isVote2 && (
                          <div className="vote2-display">
                            <small>
                              G Vote: {submission.vote_g != null ? (Number(submission.vote_g) === 1 ? 'Yes' : 'No') : 'Not voted'} | 
                              B Vote: {submission.vote_b != null ? (Number(submission.vote_b) === 1 ? 'Yes' : 'No') : 'Not voted'}
                            </small>
                          </div>
                        )}
                      </div>
                      
                      <div
                        className="vote2-comments"
                        style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
                      >
                        <div className="vote2-comments-row" style={{ display: 'block', width: '100%', marginBottom: '0.5rem' }}>
                          <span className="vote2-comments-label">Comments:</span>
                        </div>
                        <div className="vote2-comments-row" style={{ display: 'block', width: '100%', marginBottom: '0.5rem' }}>
                          <textarea
                            className="form-control"
                            rows="10"
                            style={{ display: 'block', width: '100%', boxSizing: 'border-box' }}
                            value={vote2Comments[submission.submit_id] || ''}
                            onChange={(e) => setVote2Comments({
                              ...vote2Comments,
                              [submission.submit_id]: e.target.value
                            })}
                            placeholder="Enter comments..."
                          />
                        </div>
                        <div className="vote2-comments-row" style={{ display: 'block', width: '100%' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleSaveComment(submission.submit_id)}
                            disabled={savingComment === submission.submit_id}
                          >
                            {savingComment === submission.submit_id ? 'Saving...' : 'Save Comments'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="submission-actions">
                    {/* Move to Vote1 button for submit status */}
                    {submission.status === 'submit' && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleMoveToVote1Click(submission)}
                        disabled={updatingStatus === submission.submit_id}
                      >
                        Move to Vote1
                      </button>
                    )}
                    
                    {/* Move to Vote2 button for vote1 status */}
                    {submission.status === 'vote1' && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleMoveToVote2(submission.submit_id)}
                        disabled={updatingStatus === submission.submit_id}
                      >
                        {updatingStatus === submission.submit_id ? 'Moving...' : 'Move to Vote2'}
                      </button>
                    )}
                    
                    {/* Approve/Reject buttons at bottom for vote2 */}
                    {isVote2 && (
                      <>
                        <button
                          className="btn btn-success btn-sm btn-approve"
                          onClick={() => handleApproveClick(submission)}
                          disabled={approvingId === submission.submit_id}
                        >
                          <i className="bi bi-check-circle"></i> Approve
                        </button>
                        <select
                          className="form-control form-control-sm d-inline-block w-auto ml-2 status-dropdown-select"
                          onChange={(e) => {
                            if (e.target.value) {
                              handleStatusDropdownChange(submission.submit_id, e.target.value);
                              e.target.value = ''; // Reset dropdown
                            }
                          }}
                          disabled={updatingStatus === submission.submit_id}
                        >
                          <option value="">Status...</option>
                          {otherStatuses.map(({ status, color }) => {
                            const statusColor = color && typeof color === 'string' && color.trim() ? color.trim() : null;
                            return (
                              <option
                                key={status}
                                value={status}
                                style={statusColor ? { backgroundColor: statusColor, color: '#fff' } : undefined}
                              >
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                              </option>
                            );
                          })}
                        </select>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Move to Vote1 Modal - Rule number and sub are assigned only at council approval (Approve modal) */}
        {showMoveToVote1Modal && (
          <div className="modal-overlay" onClick={() => setShowMoveToVote1Modal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Move to Vote1</h2>
              <p>Edit the rule details before moving to vote1. Rule number and sub are assigned when the council approves (vote2).</p>
              
              <div className="form-group">
                <label>Title (gr_desc):</label>
                <input
                  type="text"
                  className="form-control"
                  value={moveToVote1Data.gr_desc}
                  onChange={(e) => setMoveToVote1Data({ ...moveToVote1Data, gr_desc: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Rule Body (gr_body):</label>
                <textarea
                  className="form-control"
                  rows="5"
                  value={moveToVote1Data.gr_body}
                  onChange={(e) => setMoveToVote1Data({ ...moveToVote1Data, gr_body: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Category:</label>
                <select
                  className="form-control"
                  value={moveToVote1Data.gr_cat_id}
                  onChange={(e) => setMoveToVote1Data({ ...moveToVote1Data, gr_cat_id: e.target.value })}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.cat_id} value={cat.cat_id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowMoveToVote1Modal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleMoveToVote1}
                  disabled={updatingStatus === moveToVote1Data.submitId || !moveToVote1Data.gr_cat_id}
                >
                  {updatingStatus === moveToVote1Data.submitId ? 'Moving...' : 'Move to Vote1'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Approve Modal - Entire rule (description and body) editable before approving */}
        {showApproveModal && (
          <div className="modal-overlay" onClick={() => setShowApproveModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Approve Submission</h2>
              <p>Assign rule number and sub. You can edit the entire rule—description and body—before approving.</p>
              
              <div className="form-group">
                <label>Rule Number (gr_num):</label>
                <select
                  className="form-control"
                  value={approveData.gr_num}
                  onChange={(e) => handleGrNumChange(e.target.value)}
                  required
                >
                  <option value="">Select or enter new...</option>
                  {ruleNumbers.map(grNum => (
                    <option key={grNum} value={grNum}>
                      {grNum} (existing)
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  className="form-control mt-2"
                  value={approveData.gr_num}
                  onChange={(e) => {
                    const val = e.target.value;
                    setApproveData({ ...approveData, gr_num: val, gr_sub: '0' });
                  }}
                  placeholder={`Suggested: ${suggestedGrNum || ''}`}
                />
                <small className="form-text text-muted">
                  Select existing rule number from dropdown, or enter new number (suggested: {suggestedGrNum || 'N/A'})
                </small>
              </div>

              <div className="form-group">
                <label>Sub-Rule Number (gr_sub):</label>
                <input
                  type="number"
                  className="form-control"
                  value={approveData.gr_sub}
                  onChange={(e) => setApproveData({ ...approveData, gr_sub: e.target.value })}
                  placeholder="0 for main rule"
                  required
                />
                <small className="form-text text-muted">
                  {ruleNumbers.includes(parseInt(approveData.gr_num)) 
                    ? 'Auto-suggested based on existing rule number' 
                    : 'Enter sub-rule number'}
                </small>
              </div>

              <div className="form-group">
                <label>Description / Title (gr_desc):</label>
                <input
                  type="text"
                  className="form-control"
                  value={approveData.gr_desc}
                  onChange={(e) => setApproveData({ ...approveData, gr_desc: e.target.value })}
                  placeholder="Edit description before approving"
                  required
                />
              </div>

              <div className="form-group">
                <label>Rule Body (gr_body) — edit as needed:</label>
                <textarea
                  className="form-control"
                  rows="10"
                  value={approveData.gr_body}
                  onChange={(e) => setApproveData({ ...approveData, gr_body: e.target.value })}
                  placeholder="Edit full rule text before approving"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category:</label>
                <select
                  className="form-control"
                  value={approveData.gr_cat_id}
                  onChange={(e) => setApproveData({ ...approveData, gr_cat_id: e.target.value })}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.cat_id} value={cat.cat_id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowApproveModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success btn-approve"
                  onClick={handleApprove}
                  disabled={approvingId === approveData.submitId || !approveData.gr_num || !approveData.gr_cat_id}
                >
                  {approvingId === approveData.submitId ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}

export default SubmissionWorkflow;
